import { ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import _ from "lodash";
import { DateTime, Duration } from "luxon";
import mathUtils from "utils/mathUtils";

export enum DataType {
    Date,
    Time,
    Integer,
    Decimal,
}

export class StoreCostActions {
    public readonly id: string;
    public readonly storeId: number;
    public readonly action: string;
    public readonly storeValue: number | DateTime | null;
    public readonly targetValue: number | DateTime | null;
    public readonly targetVariance: number | Duration | null;
    public readonly clusterAverageValue: number | DateTime | null;
    public readonly dataType: DataType;
    public readonly dateId: number;

    constructor(
        id: string,
        storeId: number,
        action: string,
        storeValue: number | DateTime | null,
        targetValue: number | DateTime | null,
        targetVariance: number | Duration | null,
        clusterAverageValue: number | DateTime | null,
        dataType: DataType,
        dateId: number
    ) {
        this.id = id;
        this.storeId = storeId;
        this.action = action;
        this.storeValue = storeValue;
        this.targetValue = targetValue;
        this.targetVariance = targetVariance;
        this.clusterAverageValue = clusterAverageValue;
        this.dataType = dataType;
        this.dateId = dateId;
    }
}

export const loadClusterCostActions = (
    storesIds?: number[],
    costType?: string,
    referenceDate?: DateTime
): AppThunk<Promise<StoreCostActions[]>> => async (dispatch) => {
    if (!storesIds || !costType || !referenceDate) {
        return [];
    }
    const startDate = referenceDate.minus({ years: 1 });

    const clusterCostActionsQuery = {
        dimensions: [
            "D_CostLever.PK_CostLever",
            "F_CostLeverLines.FK_Store",
            "D_CostLever.CostLever",
            "F_CostLeverLines.ActualValue",
            "F_CostLeverLines.TargetValue",
            "D_CostLever.DataType",
            "F_CostLeverLines.FK_Date",
        ],
        timeDimensions: [{
            dimension: "D_Date.Date",
            dateRange: [startDate, referenceDate],
        }],
        filters: [
            {
                member: "D_Store.PK_Store",
                operator: "equals",
                values: storesIds
            },
            {
                member: "D_Account.AccountName",
                operator: "equals",
                values: [String(costType)]
            }
        ],
        order: [["D_CostLever.DisplayOrder", "asc"]],
    };

    const clusterCostActionsPromise = dispatch(cubeLoad(clusterCostActionsQuery));

    const result = await Promise.all([clusterCostActionsPromise]) as unknown as ResultSet[];

    const clusterCostActionsRawData = result[0].rawData();

    const data = clusterCostActionsRawData.map((item) => {
        const action: string = item["D_CostLever.CostLever"];

        let dataType: DataType;
        switch (item["D_CostLever.DataType"]) {
            case "Date":
                dataType = DataType.Date;
                break;
            case "Time":
                dataType = DataType.Time;
                break;
            case "Integer":
                dataType = DataType.Integer;
                break;
            case "Decimal":
            default:
                dataType = DataType.Decimal;
                break;
        }

        let storeValue: number | DateTime | null;
        let targetValue: number | DateTime | null;
        let targetVariance: number | Duration | null;

        switch (dataType) {
            case DataType.Date:
                storeValue = DateTime.fromFormat((item["F_CostLeverLines.ActualValue"] as string).split(".")[0], "yyyyLLdd");
                targetValue = DateTime.fromFormat((item["F_CostLeverLines.TargetValue"] as string).split(".")[0], "yyyyLLdd");
                targetVariance = storeValue.diff(targetValue, 'days').toObject().days ?? null;
                break;
            case DataType.Time:
                storeValue = DateTime.fromFormat((item["F_CostLeverLines.ActualValue"] as string).split(".")[0], "HHmm");
                targetValue = DateTime.fromFormat((item["F_CostLeverLines.TargetValue"] as string).split(".")[0], "HHmm");
                targetVariance = storeValue.diff(targetValue, ['hours', 'minutes']);
                break;
            case DataType.Integer:
                storeValue = item["F_CostLeverLines.ActualValue"] === null ? null : Number(item["F_CostLeverLines.ActualValue"]);
                targetValue = item["F_CostLeverLines.TargetValue"] === null ? null : Number(item["F_CostLeverLines.TargetValue"]);
                if (!storeValue || !targetValue) {
                    targetVariance = null;
                    break;
                }
                targetVariance = mathUtils.round(storeValue - targetValue, 0);
                break;
            case DataType.Decimal:
            default:
                storeValue = item["F_CostLeverLines.ActualValue"] === null ? null : Number(item["F_CostLeverLines.ActualValue"]);
                targetValue = item["F_CostLeverLines.TargetValue"] === null ? null : Number(item["F_CostLeverLines.TargetValue"]);
                if (!storeValue || !targetValue) {
                    targetVariance = null;
                    break;
                }
                targetVariance = mathUtils.round(storeValue - targetValue, 4);
                break;
        }

        return new StoreCostActions(
            action,
            Number(item["F_CostLeverLines.FK_Store"]),
            action,
            storeValue,
            targetValue,
            targetVariance,
            0,
            dataType,
            Number(item["F_CostLeverLines.FK_Date"])
        );
    });

    const results: StoreCostActions[] = [];

    const storeGroups = _(data).groupBy(item => item.storeId).value();
    for (const storeId in storeGroups) {
        const storeGroup = storeGroups[storeId];
        const actionGroups = _(storeGroup).groupBy(item => item.action).value();
        for (const action in actionGroups) {
            const actions = actionGroups[action];
            if (actions) {
                actions.sort((a, b) => b.dateId - a.dateId);
                results.push(actions[0]);
            }
        }
    }

    return results;
};
